<script setup lang="ts">
const props = defineProps<{
  theme: string | null | undefined
  data: {
    title?: string
    partners?: { title: string; logo?: string; svgLogo?: string; content: string; url?: string }[]
  }
}>()

// Compute rows for partners grouped into rows of 4
const rows = computed(
  () =>
    props.data.partners?.reduce(
      (acc, partner, index) => {
        const rowIndex = Math.floor(index / 4)
        if (!acc[rowIndex]) acc[rowIndex] = []
        acc[rowIndex].push(partner)
        return acc
      },
      [] as Array<typeof props.data.partners>,
    ) || [],
)
</script>

<template>
  <div class="px-bleed relative w-full text-(--theme-fg)">
    <h2
      v-if="data.title"
      class="block border-t border-(--theme-fg)/15 py-8 text-sm font-bold uppercase"
    >
      {{ data.title }}
    </h2>
    <div class="grid-container w-full gap-10">
      <!-- Loop through rows -->
      <div
        v-for="(row, rowIndex) in rows"
        :key="'row-' + rowIndex"
        :class="{
          'lg:grid-cols-1': row.length === 1,
          'lg:grid-cols-2': row.length === 2,
          'lg:grid-cols-3': row.length === 3,
          'lg:grid-cols-4': row.length === 4,
        }"
        class="grid w-full gap-(--grid-gap)"
      >
        <!-- Loop through each partner in the row -->
        <div
          v-for="(partner, index) in row"
          :key="index"
          class="box my-10 flex flex-col gap-10 border-b border-(--theme-fg)/15 pb-5 lg:gap-18"
          :class="[{ 'lg:w-cols-6 ml-auto w-full': row.length === 1 }]"
        >
          <div class="media-wrapper h-[65px] w-1/2 max-w-[50%]">
            <div v-if="partner?.svgLogo" class="object-fit h-full !w-auto">
              <!-- eslint-disable vue/no-v-html -->
              <div v-dompurify-html="partner?.svgLogo" class="svgLogo h-full w-full" />
              <!-- eslint-enable vue/no-v-html -->
            </div>
            <CommonBasicImage
              v-else-if="partner.logo"
              :image="partner.logo"
              class="!object-fit partner-media-image h-full !w-auto"
            />
          </div>
          <div class="pr-bleed grid gap-6">
            <div class="cta">
              <ElementsArrowLink
                v-if="partner.url"
                :link="{ url: partner.url, label: partner.title, target: '_blank' }"
                :class="!partner.url ? 'pointer-events-none opacity-0' : ''"
              >
                <span class="font-display text-base">{{ partner.title }}</span>
              </ElementsArrowLink>
            </div>
            <span class="lg:max-w-cols-4">
              <ElementsPortableTextWrapper v-if="partner.content" :content="partner.content" />
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style>
.svgLogo svg {
  width: auto;
  height: 100%;
}

.svgLogo svg path {
  fill: currentColor;
}
.partner-media-image img {
  width: auto;
  height: 100%;
  object-fit: contain;
}
</style>
